import { useAuth0 } from '@auth0/auth0-react';
import { useAvailableAccounts, usePermissions } from '@my/directory-api/hooks';
import { useAccount } from '@my/platform/account';
import { clearSupportSession, useSupportSession } from '@my/platform/support';
import { AccountSwitcher, Sidebar, SupportAccountLabel, type SidebarItem } from '@my/ui';
import {
  ClipboardCheck,
  LayoutDashboard,
  ListChecks,
  Settings,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '@/stores/ui';

function getDisplayName(user: ReturnType<typeof useAuth0>['user']): string {
  if (!user) return 'Demo User';
  const given = user.given_name ?? '';
  const family = user.family_name ?? '';
  const full = `${given} ${family}`.trim();
  return full || user.nickname || user.email || 'Demo User';
}

/**
 * Check-flavoured `<Sidebar>` from `@my/ui`. Holds the Check menu config and
 * bridges Auth0 → Sidebar (user, sign-out). This is intentionally a slim
 * skeleton nav for walk-around checks — add items here as Check features land.
 *
 * Account switching: composes `useAvailableAccounts` (directory-api),
 * `useAccount` (platform), and `<AccountSwitcher>` (ui) into the Sidebar's
 * `headerSlot`. Switching an account updates the platform store and refreshes
 * the Auth0 access token bound to the new `account_id`.
 */
export function AppSidebar() {
  const { isSupportSession, session: supportSession } = useSupportSession();
  const { user, logout, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { currentAccountId, setCurrentAccountId } = useAccount();
  const isOpen = useUiStore((s) => s.isSidebarOpen);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const { t } = useTranslation('navigation');

  const accountsQuery = useAvailableAccounts({
    // Not during a support session. The switcher below is REPLACED by a
    // read-only label then, so the list is never rendered — and the request
    // 404s anyway, twice, because `retry: 1` retries a deliberate answer.
    // The label was the fix for the "Loading…" that this used to cause; this
    // is the other half of it, the request that did not need making.
    enabled: isAuthenticated && !isSupportSession,
  });
  const accounts = useMemo(
    () => (accountsQuery.data ?? []).map((a) => ({ id: a.id, name: a.name })),
    [accountsQuery.data],
  );

  const handleSwitch = async (accountId: string) => {
    setCurrentAccountId(accountId);
    // Re-mint the access token bound to the newly selected account.
    // `cacheMode: 'off'` forces Auth0 to round-trip rather than returning the
    // previous token from cache.
    await getAccessTokenSilently({
      authorizationParams: {
        account_id: accountId,
        scope: 'openid profile email offline_access',
        subscription: 'check',
      },
      cacheMode: 'off',
    });
  };

  // One server-computed list drives the menu and the route guards, so the two
  // cannot disagree. Gated on the **Check app role**, not the account role:
  // the pages under Settings are app-specific, so a user promoted to Check
  // Admin while still an account-level Member should see them.
  //
  // This replaced a claim-derived role. A Stratum support token carries
  // `super_user` but no `check:<role>:<org>` entry, so the old reader fell
  // through to its lowest-privilege default and hid Settings from support —
  // the opposite of what the API allows them.
  const { can, canAny, role } = usePermissions();

  const items: SidebarItem[] = [
    {
      id: 'dashboard',
      hidden: !can('dashboard'),
      label: t('dashboard'),
      icon: LayoutDashboard,
      href: '/dashboard',
      matchRoot: true,
    },
    {
      id: 'checks',
      hidden: !can('checks'),
      label: t('checks'),
      icon: ClipboardCheck,
      href: '/checks',
    },
    {
      id: 'defects',
      hidden: !can('defects'),
      label: t('defects'),
      icon: TriangleAlert,
      href: '/defects',
    },
    {
      id: 'templates',
      hidden: !can('templates'),
      label: t('templates'),
      icon: ListChecks,
      href: '/templates',
    },
    {
      id: 'settings',
      label: t('settings'),
      icon: Settings,
      hidden: !canAny('settings.'),
      children: [
        {
          id: 'organisation',
          hidden: !can('settings.organisation'),
          label: t('organisation'),
          href: '/settings/organisation',
        },
        {
          id: 'vehicles',
          hidden: !can('settings.vehicles'),
          label: t('vehicles'),
          href: '/settings/vehicles',
        },
        {
          id: 'drivers',
          hidden: !can('settings.drivers'),
          label: t('drivers'),
          href: '/settings/drivers',
        },
        {
          id: 'usersAndAccess',
          hidden: !can('settings.usersAndAccess'),
          label: t('usersAndAccess'),
          href: '/settings/users-and-access',
        },
      ],
    },
  ];

  return (
    <Sidebar
      isOpen={isOpen}
      onToggle={toggle}
      brand={{ title: 'My Check', icon: ShieldCheck }}
      headerSlot={
        // The switcher is replaced, not hidden, during a Stratum support
        // session. It moves between the USER'S OWN accounts and reads
        // `/accounts/available`, which 404s for a support identity that is a
        // member of none — so it hung on "Loading…" forever. The read-only
        // label keeps the brand block aligned and still answers "whose data is
        // this"; changing customer stays in the support bar, because two
        // controls offering the same switch is how they end up disagreeing.
        isSupportSession ? (
          <SupportAccountLabel accountName={supportSession?.accountName} />
        ) : (
          <AccountSwitcher
            accounts={accounts}
            currentAccountId={currentAccountId}
            onSwitch={handleSwitch}
            isLoading={accountsQuery.isPending}
          />
        )
      }
      items={items}
      user={{ displayName: getDisplayName(user), role: role?.toLowerCase() ?? '' }}
      onSignOut={() => {
        // Clear FIRST. A support session lives in sessionStorage, which survives
        // a sign-out on the same tab — leaving the next person to sign in here
        // carrying somebody else's customer, and their screens pointed at that
        // customer's organisation.
        clearSupportSession({ silent: true });
        logout({ logoutParams: { returnTo: window.location.origin } });
      }}
    />
  );
}
